import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Save, Mail, CheckCircle2, AlertTriangle, ShieldAlert, Inbox, Send, Loader2, Info, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { BUILD_DATE } from "@/buildInfo";
import { setYardTz } from "@/lib/datetime";

const US_TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
];

export default function NotificationSettingsPage() {
  const [s, setS] = useState({ cart_approved_notify: "", cart_completed_notify: "", pending_completion_notify: "", backup_alert_notify: "", request_team_notify: "", critical_alert_recipients: "", sender_email: "", email_live: false });
  const [log, setLog] = useState([]);
  const [prefs, setPrefs] = useState({ labels: {}, events: {} });
  const [adminAlerts, setAdminAlerts] = useState({ labels: {}, alerts: {} });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [ver, setVer] = useState(null);
  const [tz, setTz] = useState("");
  const [tzSaving, setTzSaving] = useState(false);
  const [stuckHours, setStuckHours] = useState(null);
  const [stuckSaving, setStuckSaving] = useState(false);

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([api.get("/settings/notifications"), api.get("/settings/notifications/log"), api.get("/notifications/preferences"), api.get("/settings/admin-alerts")]);
      setS(a.data); setLog(b.data); setPrefs(c.data); setAdminAlerts(d.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/version").then(({ data }) => setVer(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/yard-timezone").then(({ data }) => setTz(data.yard_timezone)).catch(() => {}); }, []);
  useEffect(() => { api.get("/transfers/stuck-threshold").then(({ data }) => setStuckHours(data.hours)).catch(() => {}); }, []);

  const saveStuckHours = async (val) => {
    const hours = parseInt(val, 10);
    if (!Number.isInteger(hours) || hours < 1 || hours > 720) { toast.error("Enter a whole number of hours between 1 and 720."); api.get("/transfers/stuck-threshold").then(({ data }) => setStuckHours(data.hours)).catch(() => {}); return; }
    if (hours === stuckHours) return;
    setStuckSaving(true);
    try {
      const { data } = await api.post("/transfers/stuck-threshold", { hours });
      setStuckHours(data.hours);
      toast.success(`Transfer stuck warning set to ${data.hours}h.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update the threshold."); }
    finally { setStuckSaving(false); }
  };

  const saveTz = async (next) => {
    setTz(next); setTzSaving(true);
    try {
      const { data } = await api.put("/settings/yard-timezone", { yard_timezone: next });
      setYardTz(data.yard_timezone);
      toast.success(`Yard timezone set to ${data.yard_timezone}`);
    } catch (e) { toast.error("Could not update timezone"); }
    finally { setTzSaving(false); }
  };

  const toggleAdminAlert = async (event, val) => {
    const alerts = { ...adminAlerts.alerts, [event]: val };
    setAdminAlerts({ ...adminAlerts, alerts });
    try { const { data } = await api.put("/settings/admin-alerts", { alerts }); setAdminAlerts(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/notifications", {
        cart_approved_notify: s.cart_approved_notify,
        cart_completed_notify: s.cart_completed_notify,
        pending_completion_notify: s.pending_completion_notify,
        backup_alert_notify: s.backup_alert_notify,
        request_team_notify: s.request_team_notify,
        critical_alert_recipients: s.critical_alert_recipients,
        sender_email: s.sender_email,
      });
      setS(data); toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/notifications/test-alert");
      const who = (data.recipients || []).join(", ");
      if (data.sent) toast.success(`Test alert sent to ${data.recipients?.length || 0} recipient(s)`, { description: who });
      else toast.info("Nothing was emailed — this environment does not send email.", { description: `It would have gone to ${data.recipients?.length || 0} recipient(s): ${who}` });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const togglePref = async (event, val) => {
    const events = { ...prefs.events, [event]: val };
    setPrefs({ ...prefs, events });
    try { const { data } = await api.put("/notifications/preferences", { events }); setPrefs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="notification-settings-page" className="max-w-2xl">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2"><Bell className="w-6 h-6 text-[#B01E28]" />Notifications</h1>
      <p className="text-sm text-slate-500 mb-4">Who gets emailed when a pull cart is approved (warehouse) and when it's completed (service/parts).</p>

      <div className="border border-slate-200 bg-white p-4 mb-5 rounded-xl" data-testid="about-build-card">
        <div className="font-display font-bold text-slate-800 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-slate-500" />About this build</div>
        <p className="text-xs text-slate-500 mb-3">Confirm production is running the latest deploy. <b>Trust "Backend started"</b> — it changes on every redeploy and cannot lag. The frontend stamp below is written into a committed source file, so it records when the bundle was last stamped, not when this deploy went live.</p>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Frontend source stamp</dt><dd data-testid="about-frontend-build" className="font-mono text-slate-800">{BUILD_DATE}</dd></div>
          <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Backend version</dt><dd data-testid="about-backend-version" className="font-mono text-slate-800">{ver?.version || "—"}</dd></div>
          <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Backend started (UTC)</dt><dd data-testid="about-backend-started" className="font-mono text-slate-800">{ver?.started_at ? ver.started_at.slice(0, 19).replace("T", " ") : "—"}</dd></div>
        </dl>
      </div>

      <div className="border border-slate-200 bg-white p-4 mb-5 rounded-xl" data-testid="yard-timezone-card">
        <div className="font-display font-bold text-slate-800 mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-[#B01E28]" />Yard timezone</div>
        <p className="text-xs text-slate-500 mb-3">Dates and daily totals display in this timezone across audit logs, ledgers and the daily digest. Stored data always stays UTC — changing this only affects how days are shown, and Daylight Saving is handled automatically.</p>
        <div className="flex items-center gap-2">
          <select data-testid="yard-timezone-select" value={tz} disabled={tzSaving}
            onChange={(e) => saveTz(e.target.value)}
            className="rounded-lg border border-slate-200 text-sm px-2 h-9 bg-white max-w-xs disabled:opacity-50">
            {tz && !US_TIMEZONES.includes(tz) && <option value={tz}>{tz}</option>}
            {US_TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          {tzSaving && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />saving…</span>}
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-4 mb-5 rounded-xl" data-testid="alert-thresholds-card">
        <div className="font-display font-bold text-slate-800 mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-[#B01E28]" />Alert thresholds</div>
        <p className="text-xs text-slate-500 mb-3">Owner-set "warn me after N" thresholds. Set once, applies for everyone — the people a warning is about can't quietly turn it off.</p>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-slate-600 pr-3">
            <div className="font-semibold text-slate-800">Warn if a transfer hasn't been RECEIVED within</div>
            The Task Center and Floor Home flag any load — a normal transfer <b>or</b> a rejected one heading back — that's been sent this long without anyone receiving it. A rejected load's clock restarts when it's sent back.
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <input data-testid="transfer-stuck-hours-input" type="number" min={1} max={720}
              key={stuckHours ?? "h"}
              defaultValue={stuckHours ?? 48}
              onBlur={(e) => saveStuckHours(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveStuckHours(e.target.value); }}
              disabled={stuckSaving || stuckHours === null}
              className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm text-right" />
            <span className="text-xs text-slate-500">hours</span>
          </div>
        </div>
      </div>

      {/* DELIVERY IS A DIFFERENT FACT FROM CONFIGURATION. "Live email sending is active" only ever
          meant a key and a sender exist — it said nothing about whether anything arrived. */}
      {(() => {
        const d = s.delivery;
        if (!d) return null;
        const tone = d.warn ? "border-[#B01E28] bg-red-50 text-[#B01E28]"
          : { ok: "border-green-300 bg-green-50 text-green-800",
              failing: "border-[#B01E28] bg-red-50 text-[#B01E28]",
              suppressed: "border-slate-300 bg-slate-50 text-slate-600",
              not_configured: "border-amber-300 bg-amber-50 text-amber-800" }[d.state];
        const Icon = d.state === "ok" && !d.warn ? CheckCircle2 : AlertTriangle;
        return (
          <div data-testid="email-delivery-status" data-state={d.state} data-mode={d.mode} data-warn={d.warn ? "1" : "0"}
            className={`border p-3 mb-5 text-sm flex items-start gap-2 ${tone}`}>
            <Icon className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">
                {{ ok: "Email alerts are being delivered",
                   failing: "Email alerts are NOT being delivered",
                   suppressed: "This environment does not send email",
                   not_configured: "Email is not configured" }[d.state]}
                <span className="ml-2 font-mono text-[10px] uppercase opacity-70">mode: {d.mode}</span>
              </div>
              <p className="text-xs mt-0.5 opacity-90">{d.line}</p>
              {d.warn && <p className="text-xs mt-1 font-semibold" data-testid="email-delivery-warn">{d.warn}</p>}
              {d.state === "failing" && (
                <p className="text-[11px] mt-1 opacity-80">
                  In-app notifications and the audit log are unaffected — only email is stuck.
                  {d.last_sent_at ? ` Last email that did go out: ${String(d.last_sent_at).slice(0, 16).replace("T", " ")} UTC.` : ""}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      <div className={`border p-3 mb-5 text-sm flex items-center gap-2 ${s.email_live ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`} data-testid="email-status">
        {s.email_live ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
        {s.email_live ? "Resend is configured (API key + verified sender)." : "Email is in safe queued mode. Add the Resend API key + a verified sender domain to send live; recipients & in-app pick queue work now."}
      </div>

      <div className="border-2 border-[#B01E28] bg-red-50 p-4 mb-5 rounded-xl" data-testid="critical-recipients-card">
        <div className="font-display font-bold text-slate-800 mb-1 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#B01E28]" />Critical alert recipients</div>
        <p className="text-xs text-slate-600 mb-3">One editable list for your most important alerts. These addresses are emailed for <b>backup failures</b> (database <b>and</b> photos), <b>backup staleness</b>, and <b>security events</b> (new user, role/permission change, 2FA reset, failed-login spike, new-device sign-in, data export). Separate multiple addresses with commas. Active Admins always receive these too, so leaving it blank is safe.</p>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Mail className="w-3.5 h-3.5" />Who gets critical alerts</Label>
        <div className="flex gap-2 mt-1">
          <Input data-testid="critical-alert-recipients" placeholder="owner@friedrichsauto.com, manager@friedrichsauto.com" value={s.critical_alert_recipients || ""} onChange={(e) => setS({ ...s, critical_alert_recipients: e.target.value })} className="rounded-lg font-mono text-sm" />
          <Button data-testid="save-critical-recipients" onClick={save} disabled={saving} className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white shrink-0"><Save className="w-4 h-4 mr-1" />Save</Button>
          <Button data-testid="send-test-alert" onClick={sendTest} disabled={testing} variant="outline" className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-100 shrink-0">{testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send test alert</Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Sends a clearly-labelled test email to the addresses above (and all Admins) so you can confirm delivery lands in your inbox. No action needed on receipt.</p>
      </div>

      <div className="border border-slate-200 bg-white p-4 space-y-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Cart approved → notify (warehouse / pickers)</Label>
          <Input data-testid="approved-notify" placeholder="warehouse@yourshop.com, picker@…" value={s.cart_approved_notify || ""} onChange={(e) => setS({ ...s, cart_approved_notify: e.target.value })} className="rounded-lg mt-1 font-mono text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Cart completed → notify (service / parts)</Label>
          <Input data-testid="completed-notify" placeholder="service@yourshop.com" value={s.cart_completed_notify || ""} onChange={(e) => setS({ ...s, cart_completed_notify: e.target.value })} className="rounded-lg mt-1 font-mono text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Part needs identifying → notify (completion crew)</Label>
          <Input data-testid="pending-completion-notify" placeholder="foreman@yourshop.com" value={s.pending_completion_notify || ""} onChange={(e) => setS({ ...s, pending_completion_notify: e.target.value })} className="rounded-lg mt-1 font-mono text-sm" />
          <p className="text-[11px] text-slate-500 mt-1">Its own list: emailed when a part moves with only a tag and a photo. Changing the cart lists above never changes this one.</p>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5 text-[#B01E28]" />Backup FAILED → alert (admin / owner)</Label>
          <Input data-testid="backup-alert-notify" placeholder="owner@yourshop.com" value={s.backup_alert_notify || ""} onChange={(e) => setS({ ...s, backup_alert_notify: e.target.value })} className="rounded-lg mt-1 font-mono text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Inbox className="w-3.5 h-3.5" />New request → extra parts-team addresses</Label>
          <Input data-testid="request-team-notify" placeholder="parts@yourshop.com" value={s.request_team_notify || ""} onChange={(e) => setS({ ...s, request_team_notify: e.target.value })} className="rounded-lg mt-1 font-mono text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Mail className="w-3.5 h-3.5" />Sender (your verified SPF/DKIM/DMARC address)</Label>
          <Input data-testid="sender-email" placeholder="no-reply@yourdomain.com" value={s.sender_email || ""} onChange={(e) => setS({ ...s, sender_email: e.target.value })} className="rounded-lg mt-1 font-mono text-sm" />
        </div>
        <Button data-testid="save-settings" onClick={save} disabled={saving} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]"><Save className="w-4 h-4 mr-1" />Save</Button>
      </div>

      <div className="border border-slate-200 bg-white p-4 mt-5" data-testid="my-prefs">
        <div className="font-display font-bold text-slate-800 mb-1">My email preferences</div>
        <p className="text-xs text-slate-500 mb-3">In-app alerts are always on. Choose which events also email you — so you're not spammed.</p>
        <div className="divide-y divide-slate-100">
          {Object.keys(prefs.labels || {}).map((ev) => (
            <div key={ev} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-700">{prefs.labels[ev]}</span>
              <Switch data-testid={`pref-${ev}`} checked={!!prefs.events[ev]} onCheckedChange={(v) => togglePref(ev, v)} />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-4 mt-5" data-testid="admin-alerts">
        <div className="font-display font-bold text-slate-800 mb-1 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#B01E28]" />Admin security alerts</div>
        <p className="text-xs text-slate-500 mb-3">Oversight for the whole team — every security-significant event goes to all Admins (in-app always; email once live). On by default; turn off any you don't want.</p>
        <div className="divide-y divide-slate-100">
          {Object.keys(adminAlerts.labels || {}).map((ev) => (
            <div key={ev} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-700">{adminAlerts.labels[ev]}</span>
              <Switch data-testid={`admin-alert-${ev}`} checked={adminAlerts.alerts[ev] !== false} onCheckedChange={(v) => toggleAdminAlert(ev, v)} />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-slate-200 bg-white mt-5">
        <div className="px-3 py-2 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-400">Recent notifications</div>
        <div className="max-h-72 overflow-y-auto" data-testid="notif-log">
          {log.length === 0 ? <div className="p-4 text-center text-xs text-slate-400">None yet</div> :
            log.map((r) => (
              <div key={r.id} className="px-3 py-2 border-b border-slate-100 text-xs flex items-center justify-between">
                <span className="font-mono">{r.event} <span className="text-slate-400">→ {(r.recipients || []).join(", ") || "(no recipients set)"}</span></span>
                <span className={`font-mono uppercase ${r.status === "sent" ? "text-green-600" : r.status === "error" ? "text-red-600" : "text-amber-600"}`}>{r.status}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

